import { AvailabilityMonthModel, AvailabilityType } from "types/generated/models";
import { useCallback } from "react";
import { useGetQuery } from "hooks/useClientPortalQuery";
import { useClientPortalSelector, selectBookingType } from "../redux";
import { BookingType } from "types/appointmentBooking";
import { useAppointmentBookingApi } from "./useAppointmentBookingApi";

export interface IAvailabilityParams {
    locationId: number;
    serviceId: number;
    providerId: number;
    year: number;
    month: number;
    availabilityType: number;
    enableTimezones: boolean;
    clientUtcOffset: number;
    appointmentId?: number;
}

export interface IAvailabilityHook {
    availability?: AvailabilityMonthModel | null;
    isLoading: boolean;
    refetch: () => void;
}

export function useAvailability(params: IAvailabilityParams): IAvailabilityHook {
    const { getEndpoint } = useAppointmentBookingApi();
    const bookingType = useClientPortalSelector(selectBookingType);

    const queryParams = new URLSearchParams({
        locationId: params.locationId.toString(),
        providerId: params.providerId.toString(),
        year: params.year.toString(),
        month: params.month.toString(),
        availabilityType: (bookingType === BookingType.CLASS
            ? AvailabilityType.Class
            : AvailabilityType.Available
        ).toString(),
        enableTimezones: params.enableTimezones.toString(),
        clientUtcOffset: params.clientUtcOffset.toString(),
    });

    if (bookingType !== BookingType.CLASS) {
        queryParams.set("serviceId", params.serviceId.toString());
    }

    if (params.appointmentId) {
        queryParams.set("appointmentId", params.appointmentId.toString());
    }

    const shouldFetch =
        bookingType === BookingType.CLASS
            ? !!(params.locationId && typeof params.providerId === "number")
            : !!(params.locationId && params.serviceId && typeof params.providerId === "number");

    const {
        data,
        isLoading,
        refetch: queryRefetch,
    } = useGetQuery<AvailabilityMonthModel>({
        url: `${getEndpoint("GetAvailability")}?${queryParams}`,
        options: { enabled: shouldFetch },
    });

    const refetch = useCallback(() => {
        queryRefetch().catch(() => {
            console.error("Failed to refetch availability");
        });
    }, [queryRefetch]);

    return {
        availability: data,
        isLoading,
        refetch,
    };
}
